import { store } from '../../redux/store'
import { userSelectors } from '../../redux/userSlice'
import { cartSelectors, CartItemTypes } from './cartSlice'
import { mockHelper, fetchHelper } from '../../api/api'
import { CancelToken, isCancel } from 'axios'
import { isNetworkError } from '../../util/functions'

export const PaymentModes = Object.freeze({
    Plan: 'plan',
    Items: 'items',
})

export const TransactionStatus = Object.freeze({
    Completed: 'completed',
    Cancelled: 'cancelled',
})

// TODO: Use error codes from the backend
// TODO: Find a way to add the affected item to the message
const errorCodes = {
    NETWORK_ERROR:
        "A network error has caused your oder to fail. Kindly contact support if you've been charged.",
    INVALID_ORDER: 'Your order is invalid',
    PRICE_IS_NEEDED:
        'One of the items on your cart is invalid. Kindly contact support.',
    SHOULD_NOT_PAY_FOR_FREE_COURSE: 'You cannot pay for a free course.',
    SHOULD_NOT_PAY_FOR_FREE_CLASS: 'You cannot pay for a free class.',
    TOKEN_IS_USED:
        'This payment has already been attempted. Kindly reload the page and retry.',
    INVALID_TOKEN: 'This purchase is invalid. Kindly contact support.',
    INVALID_CUSTOMER:
        'Please login or sign up before attempting to purchase anything.',
    INVALID_TOTAL_PRICE:
        'The total price for your purchase is invalid. Kindly contact support.',
    INVALID_PAYMENTREF: 'This purchase is invalid. Kindly contact support.',
    ONLY_ONE_PLAN_CAN_BE_PAID_AT_A_TIME:
        'You cannot pay for more than one plan at a time.',
    TOKEN_NOT_EQUAL_TO_METADATA: 'You cannot purchase items for another user.',
    INVALID_TYPE:
        'One of the items in your cart is invalid, kindly contact support',
    INVALID_MODEL_STATE: 'This purchase is invalid. Kindly contact support.',
    NOT_ENOUGH_FREE_COURSES:
        'You do not have enough free courses to purchase the free items in your cart',
    NOT_ENOUGH_FREE_CLASSES:
        'You do not have enough free classes to purchase the free items in your cart',
    INVALID_PRICE: 'Your order is invalid.',
    PREMIUM_COURSES_ARE_NOT_FREE:
        'You cannot purchase premium courses for free',
    ACCOUNTTYPE_IS_DIFFERENT_FROM_PLANTYPE:
        'Your account type cannot subscribe to this plan. Kindly select another plan suitable for your account type',
    INVALID_PLAN: 'You cannot subscribe to this plan',
    INVALID_CLASS: 'A class in your cart does not exist',
    INVALID_COURSE: 'A course in your cart does not exist',
    DISABLE_SUB_FAILED:
        'Your previous subscription could not be disabled. Kindly try again',
    CUSTOMER_ALREADY_ENROLLED_FOR_COURSE:
        'You are already enrolled for a course in your cart',
    CUSTOMER_HAS_NO_SUBSCRIPTION_PLAN:
        'You cannot purchase items without a plan',
    REDUCE_BENEFICIARY:
        'You cannot downgrade to this plan without reducing your number of beneficiaries',
    PAID_USERS_CANNOT_USE_FREE_PLANS: 'Your order is invalid',
    PAID_USERS_CANNOT_USE_FREE_COURSES: 'Your order is invalid',
    PAID_USERS_CANNOT_USE_FREE_CLASSES: 'Your order is invalid',
    CANNOT_BUY_A_COMPLETD_OR_SUSPENDED_OR_COMPLETED_CLASS:
        'You have enrolled for one or more courses/classes in this cart',
}

export function getMessageForErrorCode(code) {
    return (
        errorCodes[code] ?? 'An unknown error occured. Please contact support.'
    )
}

function getTotalPriceForClass({ price, numInvites, numFree }) {
    return Math.max(0, numInvites - numFree) * price
}

export async function validatePayment(mode = PaymentModes.Items) {
    const state = store.getState()

    const { email } = userSelectors.userDetails(state)

    const selectedPlan = cartSelectors.selectedPlan(state)
    const totalAmount = cartSelectors.totalAmount(state)
    const appliedLoyaltyPoints = cartSelectors.appliedLoyaltyPoints(state)
    const cartItems = cartSelectors.cartItems(state)
    const cartItemDetails = cartSelectors.cartItemDetails(state)
    const selectedPlanDetails =
        mode === PaymentModes.Plan ? cartItemDetails[selectedPlan.detailId] : {}

    const items =
        mode === PaymentModes.Plan
            ? [
                  {
                      type: selectedPlan.type,
                      typeId: selectedPlan.id,
                      invites: [],
                      price: selectedPlanDetails.amount,
                      free: false,
                      numFree: 0,
                  },
              ]
            : cartItems
                  .filter(({ type }) => type !== CartItemTypes.Plan)
                  .map(item => ({
                      type: item.type,
                      typeId: item.id,
                      invites: item.invites,
                      price:
                          item.type === CartItemTypes.Course
                              ? cartItemDetails[item.cartItemId].price
                              : getTotalPriceForClass({
                                    price:
                                        cartItemDetails[item.cartItemId].price,
                                    numInvites: item.invites.length,
                                    numFree: item.numFree,
                                }),
                      free: item.free,
                      numFree: item.numFree,
                  }))

    try {
        const response = await fetchHelper.post(`/Payment/VerifyPayment`, {
            customerEmail: email,
            totalPrice:
                mode !== PaymentModes.Plan
                    ? totalAmount
                    : selectedPlanDetails.amount,
            loyaltyPoint: mode !== PaymentModes.Plan ? appliedLoyaltyPoints : 0,
            items,
        })
        const { success, token, error, reference } = response.data
        if (success)
            return {
                success: true,
                token,
                reference,
            }
        else
            return {
                success: false,
                errorCode: error.errorCode,
                itemId: error.typeId,
                type: error.type,
            }
    } catch (error) {
        if (error.response) {
            const serverError = error.response.data?.error
            return {
                success: false,
                errorCode: serverError?.errorCode,
                itemId: serverError?.typeId,
                type: serverError?.type,
            }
        } else {
            if (process.env.NODE_ENV === 'development') console.warn(error)
            return {
                success: false,
                errorCode: 'NETWORK_ERROR',
            }
        }
    }
}

const CHECK_WAIT_INTERVAL = 5000
const INITIAL_CHECK_WAIT_INTERVAL = 1000

export function waitForPaymentStatus({ token, reference }) {
    return new Promise((resolve, reject) => {
        let cancelRequest = null
        let nextCheckTimeoutId = null
        let lastRequestDone = null
        let skippedTimes = 0
        function schedule(func, initial = false) {
            nextCheckTimeoutId = setTimeout(
                func,
                initial ? INITIAL_CHECK_WAIT_INTERVAL : CHECK_WAIT_INTERVAL
            )
        }
        function unschedule(callback) {
            nextCheckTimeoutId && clearTimeout(nextCheckTimeoutId)
            callback()
        }
        async function checkStatus() {
            schedule(checkStatus)
            if (lastRequestDone === false) {
                skippedTimes++
                // Wait for about CHECK_WAIT_INTERVAL * 3 before cancelling
                // the request and making a new one
                if (skippedTimes <= 3) return
            }
            try {
                cancelRequest && cancelRequest()
                lastRequestDone = false
                skippedTimes = 0
                const response = await fetchHelper.get(
                    `/Payment/CheckPayment/${reference}`,
                    {
                        cancelToken: new CancelToken(function executor(c) {
                            cancelRequest = c
                        }),
                    }
                )
                lastRequestDone = true
                const { status, ...data } = response.data
                if (status !== 'pending') {
                    unschedule(() => resolve({ status, ...data }))
                }
            } catch (error) {
                if (isCancel(error)) {
                    // The previous request was cancelled, do nothing
                    return
                }
                if (isNetworkError(error)) {
                    // We can't continue checking if the user is offline
                    if (!navigator.onLine) {
                        unschedule(() => reject(error))
                    }
                } else {
                    unschedule(() => reject(error))
                }
            }
        }
        schedule(checkStatus, true)
    })
}

window.waitForPaymentStatus = waitForPaymentStatus

export async function completePayment({
    token,
    reference,
    status = TransactionStatus.Completed,
}) {
    const state = store.getState()

    const { loyalitypoint } = userSelectors.userDetails(state)

    try {
        if (status !== TransactionStatus.Cancelled) {
            const { status, data, error } = await waitForPaymentStatus({
                token,
                reference,
            })
            if (status === 'successful') {
                const earnedPoints = data.loyalitypoint - loyalitypoint
                return {
                    success: true,
                    earnedPoints,
                    customer: data,
                }
            } else {
                // TODO: Hook this to API
                return {
                    success: false,
                    errorCode: error.errorCode,
                }
            }
        } else {
            return { success: true }
        }
    } catch (error) {
        if (!isNetworkError(error)) {
            return {
                success: false,
                errorCode: error.response.data?.errorCode,
            }
        } else {
            if (process.env.NODE_ENV === 'development') console.warn(error)
            return {
                success: false,
                errorCode: 'NETWORK_ERROR',
            }
        }
    }
}

export async function completeFreePayment({ token, reference }) {
    const state = store.getState()

    const { loyalitypoint } = userSelectors.userDetails(state)

    try {
        const response = await fetchHelper.post(
            `/Payment`,
            {},
            {
                params: {
                    token,
                    reference,
                },
            }
        )

        const { success, data, error } = response.data

        if (success) {
            const earnedPoints = data.loyalitypoint - loyalitypoint
            return {
                success: true,
                earnedPoints,
                customer: data,
            }
        } else {
            return {
                success: false,
                errorCode: error.errorCode,
            }
        }
    } catch (error) {
        if (!isNetworkError(error)) {
            return {
                success: false,
                errorCode: error.response.data?.errorCode,
            }
        } else {
            if (process.env.NODE_ENV === 'development') console.warn(error)
            return {
                success: false,
                errorCode: 'NETWORK_ERROR',
            }
        }
    }
}
